// AGC: Reusable difficulty selector component with expandable details
// AGC: Used in PVEBattle.tsx and P2PBattle.tsx
import React, { useState } from 'react'

export type Difficulty = 'normal' | 'expert' | 'insane'

interface DifficultySelectorProps {
  selectedDifficulty: Difficulty
  onDifficultySelect: (difficulty: Difficulty) => void
  disabled?: boolean
  battleCosts: {
    entryFee: string // AGC: e.g., "50 OGB"
    buffNFTsBurned: number // AGC: Number of buff NFTs that will be burned
    winnerReceives: string // AGC: e.g., "75 OGB (stake + bounty)"
    drawRefund: string // AGC: e.g., "40 OGB each" or "25 OGB each"
    xpAwarded: string // AGC: e.g., "on-chain immediately"
  }
  variant?: 'pve' | 'pvp' // AGC: PvE or PvP variant (affects draw refund text)
}

const difficultyOptions: Record<Difficulty, { label: string; description: string; icon: string }> = {
  normal: { label: 'Normal', description: 'Bots +3 to +5 levels', icon: '🟢' },
  expert: { label: 'Expert', description: 'Bots +7 to +10 levels', icon: '🟠' },
  insane: { label: 'Insane', description: 'Bots +13 to +20 levels', icon: '🔴' },
}

const difficultyOrder: Array<Difficulty> = ['normal', 'expert', 'insane']

/**
 * AGC: Difficulty selector component with expandable battle costs details
 * AGC: Matches P2PBattle style with 3-column grid and expandable info panel
 */
export const DifficultySelector: React.FC<DifficultySelectorProps> = ({
  selectedDifficulty,
  onDifficultySelect,
  disabled = false,
  battleCosts,
  variant = 'pve',
}) => {
  const [showDetails, setShowDetails] = useState(false)

  return (
    <section data-tour="battle-difficulty-selector" className="rounded-xl bg-slate-900/80 px-3 py-2 space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-white">Select Difficulty</h2>
        <button
          type="button"
          onClick={() => setShowDetails((prev) => !prev)}
          className="flex items-center gap-1 text-[11px] font-semibold text-blue-300 transition-colors hover:text-blue-200"
        >
          {showDetails ? 'Hide details' : 'Show details'}
          <span className={`transition-transform ${showDetails ? 'rotate-180' : ''}`}>⌄</span>
        </button>
      </div>
      
      <div className="space-y-1.5">
        <div>
          <div className="grid grid-cols-3 gap-1.5">
            {difficultyOrder.map((diff) => {
              const option = difficultyOptions[diff]
              return (
                <button
                  key={diff}
                  data-tour={diff === 'normal' ? 'battle-difficulty-normal' : undefined} // AGC: Normal button for tour step 7
                  onClick={() => onDifficultySelect(diff)}
                  disabled={disabled}
                  className={`rounded-xl border px-2.5 py-1.5 text-left text-xs transition-all ${
                    selectedDifficulty === diff
                      ? 'border-violet-400/70 bg-gradient-to-r from-violet-600/70 to-indigo-600/70 text-white shadow-lg shadow-violet-500/20'
                      : 'border-slate-800 bg-slate-950/40 text-slate-200 hover:border-slate-600 hover:bg-slate-900/60'
                  }`}
                >
                  <div className="text-sm font-semibold text-white capitalize">{option.label}</div>
                  <div className="text-[11px] text-slate-300">
                    {variant === 'pve' ? option.description : `+/-${diff === 'normal' ? 5 : diff === 'expert' ? 10 : 15} levels`}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* AGC: Expandable Battle Costs Panel */}
        {showDetails && (
          <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg px-3 py-2 mt-2 space-y-2">
            <div>
              <h3 className="text-[11px] font-semibold text-blue-300 mb-1">Match Difficulty (Level Difference):</h3>
              <p className="text-slate-300 text-[11px] mb-2">
                {variant === 'pve' 
                  ? 'Difficulty determines how many levels stronger the AI bot will be. Higher difficulty = stronger bot = more XP if you win, but harder to beat.'
                  : 'Difficulty determines the level range for matchmaking. You\'ll be matched with players within the specified level difference from your level.'}
              </p>
              <ul className="text-slate-300 space-y-0.5 text-[11px] ml-2">
                {difficultyOrder.map((diff) => {
                  const option = difficultyOptions[diff]
                  return (
                    <li key={diff} className="flex items-start gap-1">
                      <span className="text-blue-300">{option.icon}</span>
                      <span><strong className="capitalize">{option.label}:</strong> {variant === 'pve' ? option.description : `±${diff === 'normal' ? 5 : diff === 'expert' ? 10 : 15} levels`}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
            <div>
              <h3 className="text-[11px] font-semibold text-blue-300 mb-1">Battle Costs:</h3>
              <ul className="text-slate-300 space-y-0.5 text-[11px]">
                <li>• {battleCosts.entryFee} entry fee</li>
                <li>• {battleCosts.buffNFTsBurned} buff NFT{battleCosts.buffNFTsBurned === 1 ? '' : 's'} burned if equipped</li>
                <li>• Winner receives {battleCosts.winnerReceives}</li>
                <li>• Draw refunds {battleCosts.drawRefund} each; NFTs still burn</li>
                <li>• XP awarded {battleCosts.xpAwarded}</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

